import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import type { AuthResponse, SubscriptionInfo, UserAccount } from '../types';
import { getFirebaseAuth } from '../firebase';

export type AuthProvider = 'php' | 'firebase';

interface LoginPayload {
  identifier: string;
  password: string;
}

interface RegisterPayload {
  fullName: string;
  phone: string;
  email?: string;
  password: string;
}

const defaultSubscription: SubscriptionInfo = {
  client_count: 0,
  subscription_status: 'free',
  active_until: null,
  plan: 'free',
  storage_quota_mb: 0,
  storage_used_mb: 0,
};

const parseJsonResponse = async <T,>(response: Response): Promise<T | null> => {
  const raw = await response.text();
  if (!raw.trim()) {
    return null;
  }
  return JSON.parse(raw) as T;
};

export const getConfiguredAuthProvider = (): AuthProvider => {
  const value = import.meta.env.VITE_AUTH_PROVIDER;
  return value === 'firebase' ? 'firebase' : 'php';
};

const loginWithPhp = async ({ identifier, password }: LoginPayload): Promise<AuthResponse> => {
  const response = await fetch('/fedapay_integration/login.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, password }),
  });

  const data = await parseJsonResponse<any>(response);
  if (!response.ok || data?.status !== 'ok') {
    throw new Error(data?.message || 'Identifiants incorrects.');
  }

  return {
    token: data.token,
    user: data.user as UserAccount,
  };
};

const registerWithPhp = async ({ fullName, phone, email, password }: RegisterPayload): Promise<AuthResponse> => {
  const response = await fetch('/fedapay_integration/register.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fullName, phone, email, password }),
  });

  const data = await parseJsonResponse<any>(response);
  if (!response.ok || data?.status !== 'ok') {
    throw new Error(data?.message || 'Impossible de creer le compte.');
  }

  return {
    token: data.token,
    user: data.user as UserAccount,
  };
};

const buildFirebaseUserAccount = async (): Promise<AuthResponse> => {
  const auth = getFirebaseAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('Aucune session Firebase active.');
  }

  const token = await currentUser.getIdToken();
  const user: UserAccount = {
    userId: currentUser.uid,
    full_name: currentUser.displayName,
    phone: currentUser.phoneNumber,
    email: currentUser.email,
    subscription: { ...defaultSubscription },
    workshop: {},
  };

  return { token, user };
};

const loginWithFirebase = async ({ identifier, password }: LoginPayload): Promise<AuthResponse> => {
  if (!identifier.includes('@')) {
    throw new Error("Le mode Firebase provisoire exige un e-mail pour la connexion.");
  }

  const auth = getFirebaseAuth();
  await signInWithEmailAndPassword(auth, identifier.trim(), password);
  return buildFirebaseUserAccount();
};

const registerWithFirebase = async ({ fullName, email, password }: RegisterPayload): Promise<AuthResponse> => {
  if (!email || !email.includes('@')) {
    throw new Error("Le mode Firebase provisoire exige un e-mail pour l'inscription.");
  }

  const auth = getFirebaseAuth();
  const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
  if (fullName.trim()) {
    await updateProfile(credential.user, { displayName: fullName.trim() });
  }

  return buildFirebaseUserAccount();
};

export const loginWithConfiguredProvider = async (payload: LoginPayload): Promise<AuthResponse> => {
  return getConfiguredAuthProvider() === 'firebase' ? loginWithFirebase(payload) : loginWithPhp(payload);
};

export const registerWithConfiguredProvider = async (payload: RegisterPayload): Promise<AuthResponse> => {
  return getConfiguredAuthProvider() === 'firebase' ? registerWithFirebase(payload) : registerWithPhp(payload);
};
